import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { OpeningHourType, StoreOpeningHourTypeSchema } from '../../../../shared/models/catalog/opening-hour-type.model';
import { OpeningHour } from '../../../../shared/models/catalog/opening-hour.model';
import { OpeningHourTypeService } from '../../../../shared/services/catalog/opening-hour-type.service';
import { applyZodErrors } from '../../../../shared/util/zod-form-errors';
import { OpeningHourRowTableComponent } from './opening-hour-row-table/opening-hour-row-table.component';

@Component({
  selector: 'app-opening-hour-type-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    TranslateModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    ToggleSwitchModule,
    ToastModule,
    ConfirmDialogModule,
    OpeningHourRowTableComponent,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './opening-hour-type-detail.component.html',
  styleUrl: './opening-hour-type-detail.component.scss',
})
export class OpeningHourTypeDetailComponent implements OnInit {
  service = inject(OpeningHourTypeService);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translate = inject(TranslateService);
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #fb = inject(FormBuilder);

  model = signal<OpeningHourType | null>(null);
  isEditMode = signal(false);
  loading = signal(false);
  submitting = signal(false);

  hours = computed<OpeningHour[]>(() => {
    const m = this.model();
    return Array.isArray(m?.opening_hours) ? (m.opening_hours as OpeningHour[]) : [];
  });

  ownerId = computed<number | null>(() => this.model()?.id ?? null);

  form: FormGroup = this.#fb.group({
    name: ['', [Validators.required, Validators.maxLength(255)]],
    public: [true as boolean],
  });

  ngOnInit(): void {
    const idParam = this.#route.snapshot.paramMap.get('id');
    if (idParam && idParam !== 'new') {
      this.isEditMode.set(true);
      this.#loadModel(Number(idParam));
    }
  }

  #loadModel(id: number) {
    this.loading.set(true);
    this.service.getById(id).subscribe({
      next: (response) => {
        this.loading.set(false);
        const item = response.data;
        this.model.set(item);
        this.form.patchValue({
          name: item.name,
          public: item.public,
        });
      },
      error: (e: { status?: number }) => {
        this.loading.set(false);
        if (e?.status === 404) {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translate.instant('label.errorHeader'),
            detail: this.#translate.instant('catalog.openingHourTypes.notFound'),
          });
          this.#router.navigate(['/catalog/opening-hour-types']);
        } else {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translate.instant('label.errorHeader'),
            detail: this.#translate.instant('catalog.openingHourTypes.loadError'),
          });
        }
      },
    });
  }

  onSubmit() {
    if (this.submitting()) return;
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const parsed = StoreOpeningHourTypeSchema.safeParse({
      name: raw.name,
      public: raw.public,
    });
    if (!parsed.success) {
      applyZodErrors(parsed.error, this.form);
      return;
    }

    this.submitting.set(true);
    const editing = this.model();

    const call$ = editing
      ? this.service.update(editing.id, parsed.data)
      : this.service.create(parsed.data);

    call$.subscribe({
      next: (response) => {
        this.submitting.set(false);
        this.model.set(response.data);
        this.#messageService.add({
          severity: 'success',
          summary: this.#translate.instant('label.successHeader'),
          detail: this.#translate.instant(
            editing
              ? 'catalog.openingHourTypes.updated'
              : 'catalog.openingHourTypes.created',
          ),
        });
        if (!editing) {
          this.isEditMode.set(true);
          this.#router.navigate([
            '/catalog/opening-hour-types',
            response.data.id,
          ]);
        }
      },
      error: (e: unknown) => {
        this.submitting.set(false);
        this.#applyServerErrors(e);
        this.#messageService.add({
          severity: 'error',
          summary: this.#translate.instant('label.errorHeader'),
          detail: this.#translate.instant(
            editing
              ? 'catalog.openingHourTypes.updateError'
              : 'catalog.openingHourTypes.createError',
          ),
        });
      },
    });
  }

  onDelete(event: Event) {
    const item = this.model();
    if (!item) return;
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      header: this.#translate.instant(
        'catalog.openingHourTypes.deleteConfirmHeader',
      ),
      message: this.#translate.instant(
        'catalog.openingHourTypes.deleteConfirmMessage',
      ),
      acceptLabel: this.#translate.instant('label.delete'),
      rejectLabel: this.#translate.instant('label.cancel'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => {
        this.service.delete(item.id).subscribe({
          next: () => {
            this.#messageService.add({
              severity: 'success',
              summary: this.#translate.instant('label.successHeader'),
              detail: this.#translate.instant('catalog.openingHourTypes.deleted'),
            });
            this.#router.navigate(['/catalog/opening-hour-types']);
          },
          error: () => {
            this.#messageService.add({
              severity: 'error',
              summary: this.#translate.instant('label.errorHeader'),
              detail: this.#translate.instant('catalog.openingHourTypes.deleteError'),
            });
          },
        });
      },
    });
  }

  onCancel() {
    this.#router.navigate(['/catalog/opening-hour-types']);
  }

  reload() {
    const item = this.model();
    if (item) this.#loadModel(item.id);
  }

  #applyServerErrors(error: unknown) {
    const errBag = (error as { error?: { errors?: Record<string, string[]> } })
      ?.error?.errors;
    if (!errBag) return;
    for (const key of Object.keys(errBag)) {
      const control = this.form.get(key);
      if (control) {
        control.setErrors({ server: { message: errBag[key][0] } });
        control.markAsTouched();
      }
    }
  }
}
